import { useState, useCallback } from 'react';
import _ from 'lodash';
import { EditRuleViewValues } from '../SelectedCategoryRulesView/types';
import { CompanyConfigurationCategories_companyConfigurationCategories_nodes } from '../__generated__/CompanyConfigurationCategories';
import {
  CompanyBusinessRuleInput,
  CompanyBusinessRuleUpdateTypeInput,
} from 'common/types/globalTypes';
import COMPANY_BUSINESS_RULE_UPDATE from './CompanyBusinessRuleUpdate.graphql';

import { useMutation } from '@apollo/client';
import BUSINESS_CATEGORY_REFRESH from '../SelectedCategoryRulesView/CompanyConfigurationCategory.graphql';
import COMPANY_CONFIGURATION_CATEGORIES from '../CompanyConfigurationCategories.graphql';
import { showCustomToast } from 'common/components/CustomToast/utils';
import {
  CompanyBusinessRuleUpdate,
  CompanyBusinessRuleUpdateVariables,
} from './__generated__/CompanyBusinessRuleUpdate';

export const useSubmitForm = () => {
  const [categoryChanged, setCategoryChanged] = useState(false);

  const [companyBusinessRuleUpdate, { loading: ruleUpdateLoading }] =
    useMutation<CompanyBusinessRuleUpdate, CompanyBusinessRuleUpdateVariables>(
      COMPANY_BUSINESS_RULE_UPDATE,
      { errorPolicy: 'all' }
    );

  const submitForm = useCallback(
    async (
      values: EditRuleViewValues,
      selectedNode: CompanyConfigurationCategories_companyConfigurationCategories_nodes
    ) => {

      const configurationCategoryId = selectedNode.configurationCategoryId;

      const companyBusinessRulesUpdate: CompanyBusinessRuleUpdateTypeInput[] =
        [];
      const companyBusinessRulesCreate: CompanyBusinessRuleInput[] = [];

      values.rules.forEach((rule) => {
        const {
          id,
          _rowTimestamp,
          layoutType,
          businessRuleTypeId,
          _isConfigEditingAllowed,
          isCategorySwitchingAllowed,
          warningMessage,
          _isDeletable,
          _isUpdatable,
          ...patch
        } = rule;

        const payloadPatch = Object.fromEntries(
          Object.entries(patch).filter(
            ([key, value]) =>
              key !== '__typename' && !(key === 'id' && value === null)
          )
        );

        if (id) {
          const existing =
            selectedNode?.companyBusinessRulesByConfigurationGroupItemId?.nodes?.find(
              (n) => n.id === id
            );

          if (existing) {
            const comparableExisting = _.pick(
              existing,
              Object.keys(payloadPatch)
            );
            const hasChanged = !_.isEqual(payloadPatch, comparableExisting);
            setCategoryChanged(
              payloadPatch.configurationCategoryId !==
                existing.configurationCategoryId
            );

            if (hasChanged) {
              companyBusinessRulesUpdate.push({
                id,
                rowTimestamp: _rowTimestamp,
                companyBusinessRulePatch: {
                  ...payloadPatch,
                  sortOrder: Number(rule.sortOrder),
                  smallintValue: Number(rule.smallintValue),
                },
              });
            }
          }
        } else {
          companyBusinessRulesCreate.push({
            configurationCategoryId:
              configurationCategoryId ?? selectedNode?.configurationCategoryId!,
            columnHint: rule.columnHint ?? '',
            sortOrder: Number(rule.sortOrder ?? 0),
            booleanValue: rule.booleanValue ?? false,
            smallintValue: Number(rule.smallintValue) ?? 0,
            numericValue: rule?.numericValue?.toString() ?? null,
            amountValue: rule?.amountValue?.toString() ?? null,
            textValue: rule.textValue ?? '',
            name: rule.name,
          });
        }
      });

      const currentIds = new Set(values.rules.map((r) => r.id).filter(Boolean));
      const companyBusinessRulesDelete: {
        id: string;
        rowTimestamp: string;
      }[] =
        selectedNode?.companyBusinessRulesByConfigurationGroupItemId?.nodes
          ?.filter((rule) => !currentIds.has(rule.id))
          .map((rule) => ({
            id: rule.id!,
            rowTimestamp: rule._rowTimestamp!,
          })) ?? [];

      const { errors } = await companyBusinessRuleUpdate({
        variables: {
          input: {
            companyConfigurationCategoriesId:
              selectedNode?.configurationCategoryId!,
            companyBusinessRulesUpdate,
            companyBusinessRulesCreate,
            companyBusinessRulesDelete,
          },
        },
        awaitRefetchQueries: true,
        refetchQueries: categoryChanged
          ? [
              {
                query: BUSINESS_CATEGORY_REFRESH,
                variables: { id: selectedNode.id },
              },
            ]
          : [
              {
                query: COMPANY_CONFIGURATION_CATEGORIES,
              },
            ],
      });

      if (errors?.length) {
        showCustomToast({ content: errors[0].message, appearance: 'error' });
        return false;
      } else {
        showCustomToast({
          content: 'Business rules saved successfully',
          appearance: 'success',
        });
        return true;
      }
    },
    []
  );

  return {
    categoryChanged,
    ruleUpdateLoading,
    submitForm,
  };
};
